import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { map, Observable } from 'rxjs';
import {
  MFA_DISABLE,
  MFA_RECOVERY_CODES_REGENERATE,
  MFA_SETUP_CONFIRM,
  MFA_SETUP_START,
  MFA_STATUS_QUERY,
} from 'src/app/graphql/mfa.graphql';

// Mensagem devolvida pelo Bifrost -> chave de tradução em assets/i18n
const MFA_ERROR_KEYS: { [message: string]: string } = {
  'Invalid or already used MFA code.': 'mfa.error-code-invalid',
  'Too many failed MFA code attempts. Try again later.': 'mfa.error-too-many',
  'MFA is already enabled for this user.': 'mfa.error-already-enabled',
  'MFA setup was not started for this user.': 'mfa.error-not-started',
  'Invalid username or password': 'mfa.error-password-invalid',
};

@Injectable({
  providedIn: 'root',
})
export class MfaService {
  constructor(private apollo: Apollo) {}

  /** Retorna true se o usuário já tem o 2FA ativo. */
  getStatus(username: string | null): Observable<boolean> {
    return this.apollo
      .query<any>({
        query: MFA_STATUS_QUERY,
        variables: { username },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => !!result.data?.user?.edges?.[0]?.node?.mfaEnabled));
  }

  /** Gera um segredo novo e devolve o QR code (data:image/png;base64,...). */
  setupStart(): Observable<string> {
    return this.apollo
      .mutate<any>({ mutation: MFA_SETUP_START })
      .pipe(map((result) => result.data?.mfaSetupStart?.qrCode ?? ''));
  }

  /** Confirma o cadastro e devolve os códigos de recuperação (exibidos uma única vez). */
  setupConfirm(code: string): Observable<string[]> {
    return this.apollo
      .mutate<any>({ mutation: MFA_SETUP_CONFIRM, variables: { code } })
      .pipe(map((result) => result.data?.mfaSetupConfirm?.recoveryCodes ?? []));
  }

  /** Gera códigos de recuperação novos; os anteriores deixam de valer. */
  regenerateRecoveryCodes(code: string): Observable<string[]> {
    return this.apollo
      .mutate<any>({ mutation: MFA_RECOVERY_CODES_REGENERATE, variables: { code } })
      .pipe(map((result) => result.data?.mfaRecoveryCodesRegenerate?.recoveryCodes ?? []));
  }

  /** Desativa o 2FA. Retorna o mfaEnabled final (false em caso de sucesso). */
  disable(password: string, code: string): Observable<boolean> {
    return this.apollo
      .mutate<any>({ mutation: MFA_DISABLE, variables: { password, code } })
      .pipe(map((result) => !!result.data?.mfaDisable?.user?.mfaEnabled));
  }

  /** Converte o erro do Apollo na chave de tradução da mensagem a exibir. */
  errorKey(error: any): string {
    const message: string =
      error?.errors?.[0]?.message ??
      error?.graphQLErrors?.[0]?.message ??
      error?.message ??
      '';
    const found = Object.keys(MFA_ERROR_KEYS).find((m) => message.includes(m));
    return found ? MFA_ERROR_KEYS[found] : 'mfa.error-generic';
  }
}
